/**
 * Shapes of the OneFleet endpoints the module calls. WP-U3 replaces the hand-written part with types generated
 * by `openapi-typescript` from OneFleet's committed `/int/v1` snapshot (`docs/api/int-v1.json`, TS-07); the
 * system endpoints below come from `apps/api/src/of_api/main.py` and `packages/of_core/src/of_core/{health,version}.py`
 * and are validated with zod at the boundary, the way `lib/api.ts` validates Frappe answers.
 */
import { z } from "zod";

/** `?expand=erp` on a list (D-90): the row's ERP links and a derived state — the register's badge column. */
export const ErpExpandSchema = z.object({
  state: z.string(),
  links: z.array(
    z.object({
      id: z.string(),
      erp_system: z.string(),
      erp_doctype: z.string(),
      erp_id: z.string(),
      erp_url: z.string().nullable(),
      sync_state: z.string(),
      last_synced_at: z.string().nullable().optional(),
      updated_at: z.string().optional(),
    }),
  ),
});
export type ErpExpand = z.infer<typeof ErpExpandSchema>;

export const HealthSchema = z.object({ status: z.literal("ok") });
export type Health = z.infer<typeof HealthSchema>;

const ProbeSchema = z
  .object({ ok: z.boolean(), error: z.string().optional() })
  .passthrough();

/** The worker's last heartbeat as `/readyz` reports it (OneFleet D-71): fresh while younger than 150 s. */
export const WorkerHeartbeatSchema = z
  .object({
    ok: z.boolean(),
    seen_at: z.string().nullable(),
    age_s: z.number().nullable(),
    relay: z.boolean().optional(),
    queues: z.array(z.string()).optional(),
    version: z.string().optional(),
    error: z.string().optional(),
  })
  .passthrough();
export const ReadinessSchema = z.object({
  status: z.enum(["ready", "degraded"]),
  postgres: ProbeSchema,
  valkey: ProbeSchema,
  worker: WorkerHeartbeatSchema.optional(),
});
export type Readiness = z.infer<typeof ReadinessSchema>;

export const VersionSchema = z.object({
  service: z.string(),
  version: z.string(),
  git_sha: z.string().optional(),
  built_at: z.string().optional(),
  started_at: z.string().optional(),
  python: z.string().optional(),
});
export type Version = z.infer<typeof VersionSchema>;
